/**
 * Path predicates for maintenance mode (TX-4910).
 *
 * Same shape and reasoning as `routeAccess.ts`: a pure module with no imports,
 * so the proxy and the API gate share one allow-list, the middleware bundle
 * stays small, and the list is unit-testable on its own.
 *
 * Paths here keep working while maintenance is on. Everything else gets a 503.
 */

/**
 * Prefixes that stay up during maintenance, and why each one has to.
 *
 * - `/admin`, `/api/v1/admin` — admins are never locked out; also where the
 *   "maintenance is live" banner lives.
 * - `/sign-in`, `/sso-callback`, `/sso-google-callback`, `/accept-token` — an
 *   admin who is signed out still has to be able to sign in. This is the
 *   safety exemption to "everything except admin", not a scope change.
 * - `/api/v1/webhooks` — Stripe and Clerk retries are finite. Dropping them
 *   loses subscription and identity state for real.
 * - `/api/v1/cron` — Vercel crons.
 * - `/api/health` — the liveness probe. A 503 here reads as "the deployment is
 *   broken", which is not what a planned window means.
 */
const MAINTENANCE_EXEMPT_PREFIXES = [
  "/admin",
  "/api/v1/admin",
  "/sign-in",
  "/sso-callback",
  "/sso-google-callback",
  "/accept-token",
  "/api/v1/webhooks",
  "/api/v1/cron",
  "/api/health",
] as const;

/**
 * True when `pathname` keeps working with maintenance on.
 *
 * Matches on **segment boundaries**, not bare `startsWith`. A plain prefix
 * match would exempt `/api/v1/administrivia` along with `/api/v1/admin`, so any
 * future route whose name happens to start with an exempt one would silently
 * escape maintenance. This list decides who stays online; it does not get to be
 * approximate.
 */
export function isMaintenanceExemptPath(pathname: string): boolean {
  return MAINTENANCE_EXEMPT_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

/**
 * True for requests that expect JSON rather than a page.
 *
 * Drives the shape of the 503 only: an API caller gets `{"error":"maintenance"}`,
 * a browser gets the HTML page. Both carry `Retry-After`.
 */
export function isMaintenanceApiPath(pathname: string): boolean {
  return pathname.startsWith("/api/");
}

/** Query param that carries the bypass secret; also the cookie name. */
export const MAINTENANCE_BYPASS_PARAM = "maintenance-bypass";
export const MAINTENANCE_BYPASS_COOKIE = "sukha-maintenance-bypass";

/**
 * `Retry-After`, in seconds. An hour: long enough that crawlers back off,
 * short enough that they come back the same day. Keep real windows under a
 * day — a 503 that persists for days does start to affect indexing.
 */
export const MAINTENANCE_RETRY_AFTER_SECONDS = 3600;
